import {
  STANDING_RESPONSE_STATUSES,
  describeEstimateResponse,
} from './estimateResponseProvenance';
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from '../notices/noticeReceiptMath';
import { tenantIdForNoticeWrite } from '../notices/noticeReceiptsSession';
import { formatPersonName } from '../format/formatPersonName';

/**
 * RD-NOTICES-V2 — server write-through for this store's two notice_keys:
 *   `estimate_response_seen`        — acked_at = the seen watermark
 *   `estimate_response:<estimateId>` — announced_at / acked_at per response
 *
 * Every read-side helper below is unchanged; these three additions are
 * ADDITIVE (existing call sites keep working with no `opts` arg):
 *   1. each local write function takes an optional trailing `opts.skipSync`
 *      (used only by the merge-from-server path, to avoid re-uploading data
 *      that just arrived FROM the server);
 *   2. `mergeEstimateResponseReceiptsFromServer` applies server rows locally,
 *      MONOTONICALLY — it reuses the same write functions, whose existing
 *      `at >= prev` guards already implement "never move a timestamp back";
 *   3. `collectEstimateResponseReceiptsForUpload` snapshots everything local
 *      for the one-time post-hydration upload.
 */
function syncEstimateReceiptForId(userId, estimateId, storage) {
  const tenantId = tenantIdForNoticeWrite(userId);
  if (!tenantId) return;
  // Full current stamps for this id, not just the field that just changed —
  // an upsert replaces the whole row, so sending only one field would null
  // out the other on the server.
  const announced = readEstimateResponseAnnounced(userId, storage);
  const acked = readEstimateResponseAcked(userId, storage);
  upsertNoticeReceipts({
    tenantId,
    userId,
    rows: [{
      notice_key: `estimate_response:${estimateId}`,
      announced_at: announced[estimateId] || null,
      acked_at: acked[estimateId] || null,
    }],
  });
}

/**
 * RD-EST-RESP-001 — the DECISION LAYER behind the "Customer responses" summary
 * modal (the founder's chosen Option 1: a centered modal on dashboard entry,
 * "shown ONLY when something new happened since you last looked — never on an
 * ordinary sign-in, so it stays news, not noise").
 *
 * Everything here is PURE. The hook does the I/O; this file decides who may see
 * the modal, which rows count as unseen, and whether to open at all — so those
 * three questions are unit-testable without a database, a clock, or a browser.
 *
 * *** THE PREDICATE IS NOT NEGOTIABLE (migration 20260831120000, PHASE 2+ OWES
 * item 13). *** "Unseen" is `hasStandingCustomerResponse(row) AND response_at >
 * lastSeen`, never `response_source IS NOT NULL AND response_at > lastSeen`.
 * The naive version is satisfied FOREVER by every returned-to-Sent estimate:
 * "Return to Sent" keeps the stale response row and only stamps
 * `response_superseded_at`, so a superseded response would resurface in this
 * modal on every single dashboard entry, announcing an approval that no longer
 * stands. `describeEstimateResponse` carries that rule; nothing here re-derives
 * it from the raw `response_*` columns.
 */

/**
 * Roles that may see this modal.
 *
 * *** `operations` IS EXCLUDED DELIBERATELY AND MUST STAY EXCLUDED. *** Ops
 * rides the ADMIN shell (`AdminHome`), so "mount it on the admin dashboard" is
 * NOT the same statement as "admin only" — the mount point cannot express this
 * boundary and a role check must. Ops is money-blind by construction (zero
 * estimate SELECT, zero invoice SELECT) and every row here carries an estimate
 * total and a customer's decision. The server would return nothing anyway; this
 * is the UI half of the two-doors rule, not the boundary itself.
 *
 * Supervisor and worker are excluded for the same reason. Platform has no
 * tenant at all.
 */
export const ESTIMATE_RESPONSE_SUMMARY_ROLES = Object.freeze(['admin', 'billing']);

export function canSeeEstimateResponseSummary(role) {
  return ESTIMATE_RESPONSE_SUMMARY_ROLES.includes(String(role || ''));
}

/** Most rows we will ever render. A long list is a report, not a greeting. */
export const ESTIMATE_RESPONSE_SUMMARY_MAX_ROWS = 8;

/** First-ever visit (no stamp): still show genuinely recent news, not history. */
const FIRST_VISIT_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;
/** Hard floor for ANY window, however old the stamp is. */
const MAX_LOOKBACK_MS = 30 * 24 * 60 * 60 * 1000;

/** localStorage keys are per USER, never per tenant or per device alone. */
export function estimateResponseSeenKey(userId) {
  return `cvx-estimate-response-seen-v1:${userId || 'anon'}`;
}

/**
 * The per-device "last looked" stamp.
 *
 * localStorage is the founder-sanctioned home (the bridge says per-device is
 * acceptable): it is a READ-RECEIPT, not tenant data — an ISO instant and
 * nothing else, no names, no amounts, no ids. Keying it per user id is what
 * keeps a shared office iPad from showing one account's "new since your last
 * visit" to the next person who signs in; without the suffix the two accounts
 * would silently share one receipt.
 *
 * Fail-soft in BOTH directions (Safari private mode throws on setItem, and a
 * corrupted value must not crash a dashboard): an unreadable stamp reads as
 * "never looked", an unwritable one just means the modal may appear once more.
 */
export function readEstimateResponseSeenAt(userId, storage) {
  // No user id → no per-user bucket to read. Never fall through to a shared
  // `:anon` key, which would leak one signed-in-less caller's "last looked"
  // stamp into another's read.
  if (!userId) return null;
  try {
    const raw = (storage || window.localStorage).getItem(estimateResponseSeenKey(userId));
    if (!raw) return null;
    // Reject anything that is not a parseable instant rather than letting it
    // flow into a `>` comparison, where NaN silently makes every row unseen.
    return Number.isNaN(Date.parse(raw)) ? null : raw;
  } catch {
    return null;
  }
}

export function writeEstimateResponseSeenAt(userId, isoInstant, storage, opts = {}) {
  // No user id → no-op, never a shared `:anon` write.
  if (!userId) return false;
  try {
    // MONOTONIC (class B): the watermark only moves forward.
    const next = maxIso(readEstimateResponseSeenAt(userId, storage), isoInstant);
    if (!next) return false;
    (storage || window.localStorage).setItem(estimateResponseSeenKey(userId), next);
    if (!opts.skipSync) {
      const tenantId = tenantIdForNoticeWrite(userId);
      if (tenantId) {
        upsertNoticeReceipts({
          tenantId, userId, rows: [{ notice_key: 'estimate_response_seen', acked_at: next }],
        });
      }
    }
    return true;
  } catch {
    return false;
  }
}

/** Per-response acknowledgements. A high-water instant cannot clear one row. */
export function estimateResponseAckedKey(userId) {
  return `cvx-estimate-response-acked-v1:${userId || 'anon'}`;
}

/**
 * `{ estimateId: responseAtIso }`. An entry hides that response only. A later
 * `response_at` on the same estimate (sent again, answered again) shows.
 * Entries older than the summary's 30-day floor are dropped on read.
 */
function readStampMap(storageKey, userId, storage, nowMs) {
  // No user id → no per-user bucket, never a shared `:anon` read.
  if (!userId) return {};
  try {
    const raw = (storage || window.localStorage).getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    Object.keys(parsed).forEach((id) => {
      const at = Date.parse(parsed[id]);
      if (!id || Number.isNaN(at) || nowMs - at > MAX_LOOKBACK_MS) return;
      out[id] = new Date(at).toISOString();
    });
    return out;
  } catch {
    return {};
  }
}

export function readEstimateResponseAcked(userId, storage, nowMs = Date.now()) {
  return readStampMap(estimateResponseAckedKey, userId, storage, nowMs);
}

/**
 * The dashboard greeting is once per response. Later writes this map and does
 * NOT acknowledge, so the estimates-tab marks and the nav badge stay. A newer
 * `response_at` on the same estimate is news again and may greet once more.
 * Review from the estimates tab ignores this map.
 */
export function estimateResponseAnnouncedKey(userId) {
  return `cvx-estimate-response-announced-v1:${userId || 'anon'}`;
}

export function readEstimateResponseAnnounced(userId, storage, nowMs = Date.now()) {
  return readStampMap(estimateResponseAnnouncedKey, userId, storage, nowMs);
}

export function announceEstimateResponses(userId, rows, storage, nowMs = Date.now(), opts = {}) {
  // No user id → no-op, never a shared `:anon` write.
  if (!userId) return false;
  try {
    const store = storage || window.localStorage;
    const next = readEstimateResponseAnnounced(userId, store, nowMs);
    const touched = [];
    (rows || []).forEach((row) => {
      const at = Date.parse(row?.respondedAt || '');
      if (!row?.id || Number.isNaN(at)) return;
      const prev = next[row.id] ? Date.parse(next[row.id]) : 0;
      if (at >= prev) { next[row.id] = new Date(at).toISOString(); touched.push(row.id); }
    });
    store.setItem(estimateResponseAnnouncedKey(userId), JSON.stringify(next));
    if (!opts.skipSync) touched.forEach((id) => syncEstimateReceiptForId(userId, id, store));
    return true;
  } catch {
    return false;
  }
}

function isStampedResponse(stamps, estimateId, respondedAtMs) {
  const stamped = Date.parse(stamps?.[estimateId] || '');
  return !Number.isNaN(stamped) && respondedAtMs <= stamped;
}

/** Drop rows the dashboard already greeted. Review (`present`) does not call this. */
export function omitAnnouncedResponses(rows, announced) {
  return (rows || []).filter((r) => !isStampedResponse(announced, r.id, r.respondedAtMs));
}

export function acknowledgeEstimateResponse(userId, estimateId, responseAtIso, storage, nowMs = Date.now(), opts = {}) {
  // No user id → no-op, never a shared `:anon` write.
  if (!userId) return false;
  if (!estimateId || !responseAtIso) return false;
  const at = Date.parse(responseAtIso);
  if (Number.isNaN(at)) return false;
  try {
    const store = storage || window.localStorage;
    const next = readEstimateResponseAcked(userId, store, nowMs);
    const prev = next[estimateId] ? Date.parse(next[estimateId]) : 0;
    if (at >= prev) next[estimateId] = new Date(at).toISOString();
    store.setItem(estimateResponseAckedKey(userId), JSON.stringify(next));
    if (!opts.skipSync) syncEstimateReceiptForId(userId, estimateId, store);
    return true;
  } catch {
    return false;
  }
}

/**
 * Server rows → local store, MONOTONICALLY. Reuses the write functions above
 * in `skipSync` mode — their existing "at >= prev" guards ARE the merge rule,
 * so a stale/out-of-order server row can never overwrite a newer local
 * stamp (the next upload carries the newer local value back up).
 */
export function ownsEstimateResponseNoticeKey(noticeKey) {
  return noticeKey === 'estimate_response_seen'
    || (typeof noticeKey === 'string' && noticeKey.startsWith('estimate_response:'));
}

export function mergeEstimateResponseReceiptsFromServer(userId, rows, storage) {
  if (!userId || !Array.isArray(rows)) return;
  rows.forEach((row) => {
    const key = row?.notice_key;
    if (!ownsEstimateResponseNoticeKey(key)) return;
    if (key === 'estimate_response_seen') {
      const current = readEstimateResponseSeenAt(userId, storage);
      if (isNewerIso(row.acked_at, current)) {
        writeEstimateResponseSeenAt(userId, row.acked_at, storage, { skipSync: true });
      }
      return;
    }
    const id = key.slice('estimate_response:'.length);
    if (!id) return;
    if (row.announced_at) {
      announceEstimateResponses(userId, [{ id, respondedAt: row.announced_at }], storage, Date.now(), { skipSync: true });
    }
    if (row.acked_at) {
      acknowledgeEstimateResponse(userId, id, row.acked_at, storage, Date.now(), { skipSync: true });
    }
  });
}

/** Snapshot of every local receipt this store owns, for the one-time
 *  post-hydration upload of local-only/newer-local data. */
export function collectEstimateResponseReceiptsForUpload(userId, storage) {
  if (!userId) return [];
  const rows = [];
  const seenAt = readEstimateResponseSeenAt(userId, storage);
  if (seenAt) rows.push({ notice_key: 'estimate_response_seen', acked_at: seenAt });
  const announced = readEstimateResponseAnnounced(userId, storage);
  const acked = readEstimateResponseAcked(userId, storage);
  const ids = new Set([...Object.keys(announced), ...Object.keys(acked)]);
  ids.forEach((id) => {
    rows.push({
      notice_key: `estimate_response:${id}`,
      announced_at: announced[id] || null,
      acked_at: acked[id] || null,
    });
  });
  return rows;
}

function isAckedResponse(acked, estimateId, respondedAtMs) {
  return isStampedResponse(acked, estimateId, respondedAtMs);
}

/** Public alias — used by `useEstimateResponseSummary` to filter an OPEN
 *  modal's rows when a remote session acks one (RD-NOTICES-V2): rows may
 *  leave an open modal, never be added, so this is a pure re-check against
 *  the (already bus-merged) local acked map, not a refetch. */
export { isAckedResponse as isEstimateResponseAcked };

/**
 * The ISO instant the QUERY should start from.
 *
 * Two clamps, each for a different failure:
 *   • no stamp  → 7 days, so a fresh device/browser is greeted with news rather
 *     than with every response the tenant has ever received.
 *   • old stamp → never further back than 30 days, so an account that has not
 *     opened the app since spring does not open onto a wall of rows (and the
 *     query stays bounded regardless of what is in storage).
 */
export function resolveSummaryWindowStart(lastSeenIso, nowMs = Date.now()) {
  const floor = nowMs - MAX_LOOKBACK_MS;
  const parsed = lastSeenIso ? Date.parse(lastSeenIso) : NaN;
  const start = Number.isNaN(parsed) ? nowMs - FIRST_VISIT_LOOKBACK_MS : parsed;
  return new Date(Math.max(start, floor)).toISOString();
}

/**
 * Rows → view models, newest first, capped.
 *
 * Three filters, in order, and each one earns its place:
 *   1. STANDING status — the OWES-13 predicate, via `describeEstimateResponse`.
 *   2. `kind === 'online'` — a staff-marked accept is not a customer response
 *      and has no business in a modal titled "Customer responses".
 *   3. `response_at > lastSeen` — strictly greater, matching the standing
 *      predicate's own boundary rule, so the row you just dismissed (stamped at
 *      exactly its own `response_at`) does not come straight back.
 *
 * A row whose `response_at` is unparseable is DROPPED rather than shown: we
 * cannot honestly say when it happened, and it would otherwise be permanently
 * unseen (no stamp can ever exceed NaN).
 *
 * *** RETURNS `{ rows, totalUnseen }`, NOT A BARE ARRAY, AND THAT IS THE WHOLE
 * POINT. *** The cap drops the OLDEST unseen rows, and `dismiss` then stamps the
 * NEWEST row shown — which sits above every dropped row, so the tail is marked
 * read having never been rendered. That is precisely the loss
 * `newestResponseInstant`'s own docblock says it exists to prevent, reintroduced
 * by the cap. A single high-water-mark stamp cannot avoid it (any stamp low
 * enough to preserve the tail also re-shows the rows just dismissed), so the
 * honest fix is to make the loss VISIBLE: the caller renders `totalUnseen` in
 * the subtitle and an explicit "and N more" line. Returning only the capped
 * array made the modal claim "8 new" when 12 arrived.
 *
 * `totalUnseen` is bounded by what the caller fetched (the hook over-fetches
 * `MAX_ROWS * 3`), so beyond ~24 simultaneous unseen responses it under-counts.
 * Documented rather than papered over — every one of those rows is still on the
 * estimates list with its status pill.
 */
export function selectUnseenEstimateResponses(rows, lastSeenIso, nowMs = Date.now(), acked = null) {
  const since = Date.parse(resolveSummaryWindowStart(lastSeenIso, nowMs));
  const all = (rows || [])
    .map((row) => {
      const described = describeEstimateResponse(row);
      if (described.kind !== 'online') return null;
      const at = Date.parse(described.respondedAt || '');
      if (Number.isNaN(at) || at <= since) return null;
      if (isAckedResponse(acked, row.id, at)) return null;
      return {
        id: row.id,
        estimateId: row.id,
        customerName: formatPersonName(row.customers?.full_name) || '',
        estimateNumber: row.estimate_number,
        estimateNumberYear: row.estimate_number_year,
        total: row.total,
        decision: described.decision,
        signedName: described.signedName,
        declineReason: described.declineReason,
        respondedAt: described.respondedAt,
        respondedAtMs: at,
        estimate: row,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.respondedAtMs - a.respondedAtMs);

  return {
    // The greeting renders `rows` only. `allRows` is every unseen response in
    // this fetch, so the list can mark the tail the card does not draw.
    rows: all.slice(0, ESTIMATE_RESPONSE_SUMMARY_MAX_ROWS),
    allRows: all,
    totalUnseen: all.length,
  };
}

/**
 * The stamp to write on dismiss: the NEWEST response actually shown.
 *
 * Deliberately NOT `Date.now()`. A response that lands between the fetch and
 * the tap would be swallowed by a wall-clock stamp — marked read without ever
 * having been rendered. Stamping what was shown means the next entry surfaces
 * it. Falls back to the current time only when there is nothing to stamp.
 *
 * *** IT IS A SINGLE HIGH-WATER MARK, AND THAT COSTS TWO KNOWN LOSSES. *** Both
 * are accepted, and neither loses the response itself — every one of them is
 * still on the estimates list with its status pill; what is lost is only the
 * greeting.
 *
 *   1. THE CAPPED TAIL. `selectUnseenEstimateResponses` slices to
 *      `ESTIMATE_RESPONSE_SUMMARY_MAX_ROWS`, and the stamp covers all of them.
 *      Answered rather than hidden: `totalUnseen` is the TRUE count and the
 *      modal says "and N more".
 *   2. AN OUT-OF-ORDER ARRIVAL (new with the LIVE trigger). A response held
 *      while the modal is open, whose `response_at` is EARLIER than the newest
 *      row on screen, sits below the stamp this writes and is filtered out by
 *      the flush's `.gt('response_at', …)`. It needs two customers answering
 *      inside one viewing window AND the later-committed one carrying the
 *      earlier timestamp — realtime delivers in commit order, so the two must
 *      genuinely cross.
 *
 * Closing (2) means stamping by ROW ID instead of by instant: remember the ids
 * just stamped, widen the window back to the OLDEST row shown, and exclude by
 * id. Deliberately not done — it doubles the state the stamp carries to buy a
 * coin-flip race. If a THIRD loss ever appears here, that is the signal that the
 * high-water mark has stopped paying for itself.
 */
export function newestResponseInstant(viewRows, nowMs = Date.now()) {
  const newest = (viewRows || []).reduce(
    (max, r) => (Number.isFinite(r?.respondedAtMs) && r.respondedAtMs > max ? r.respondedAtMs : max),
    0,
  );
  return new Date(newest > 0 ? newest : nowMs).toISOString();
}

/** Statuses the query narrows to — re-exported so the hook has ONE import. */
export { STANDING_RESPONSE_STATUSES };
